import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import feather from 'feather-icons';
import { ToastrService } from 'ngx-toastr';
import {
  LogicielCaisseReasortTransfertService,
  REASORT_IS,
  ReasortTransfertTableRow,
} from '../../../../../services/logiciel-caisse/logiciel-caisse-reasort-transfert.service';
import { ReassortTab } from '../reassort.component';

@Component({
  selector: 'app-reassort-content',
  templateUrl: './reassort-content.component.html',
  styleUrls: ['./reassort-content.component.scss'],
})
export class ReassortContentComponent implements OnInit, AfterViewInit, OnDestroy {
  tab: ReassortTab = 'en-cours';
  loading = false;
  rows: ReasortTransfertTableRow[] = [];
  searchTerm = '';
  dateDebut = '';
  dateFin = '';
  draftDateDebut = '';
  draftDateFin = '';
  showFilter = false;

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  serverTotal = 0;

  readonly skeletonRows = Array.from({ length: 8 });

  private searchDebounce?: ReturnType<typeof setTimeout>;
  private loadSub?: Subscription;
  private routeSub?: Subscription;
  private refreshSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reasortService: LogicielCaisseReasortTransfertService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.url.subscribe((segments) => {
      const path = segments[0]?.path;
      this.tab = path === 'clos' ? 'clos' : 'en-cours';
      this.currentPage = 1;
      this.loadPage();
    });
    this.refreshSub = this.reasortService.onListRefresh().subscribe(() => {
      this.currentPage = 1;
      this.loadPage();
    });
  }

  ngAfterViewInit(): void {
    this.refreshIcons();
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.routeSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  get pageFrom(): number {
    if (!this.serverTotal) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.serverTotal);
  }

  cell(row: ReasortTransfertTableRow, key: keyof ReasortTransfertTableRow): string {
    const v = row[key];
    if (v == null || v === '') {
      return '—';
    }
    if (key === 'QUANTITÉ') {
      const n = Number(String(v).replace(',', '.'));
      if (Number.isFinite(n)) {
        return String(Math.round(n));
      }
    }
    if (key === 'STATUT') {
      const is = Number(row._is_reception);
      if (is === REASORT_IS.ANNULE) {
        return 'Archivé';
      }
    }
    return String(v);
  }

  canArchiver(row: ReasortTransfertTableRow): boolean {
    const is = Number(row._is_reception);
    // Aligné backend : archivable si Défaut (0) ou Créé / en cours transfert (2)
    if (is === REASORT_IS.DEFAUT || is === REASORT_IS.CREE || is === REASORT_IS.EN_COURS_TRANSFERT) {
      return true;
    }
    const statut = String(row.STATUT || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
    return statut === 'cree' || statut === 'defaut' || statut === 'created';
  }

  archiver(row: ReasortTransfertTableRow, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0 || !this.canArchiver(row)) {
      return;
    }
    const ok = window.confirm(
      `Archiver le réassort ${this.cell(row, 'N° DOCUMENT')} ? (uniquement s’il n’est pas encore approuvé)`
    );
    if (!ok) {
      return;
    }
    this.reasortService.archiverTransfert(id).subscribe({
      next: () => {
        this.toastr.success('Réassort archivé.', 'Réassort');
        this.reasortService.notifyListRefresh();
        this.loadPage();
      },
      error: (err: Error) => {
        this.toastr.error(err?.message || 'Échec de l’archivage', 'Erreur');
      },
    });
  }

  statusBadgeClass(row: ReasortTransfertTableRow): string {
    const is = Number(row._is_reception);
    if (is === REASORT_IS.RECEPTION_TERMINEE) {
      return 'badge-light-success';
    }
    if (is === REASORT_IS.EN_COURS_LIVRAISON) {
      return 'badge-light-warning';
    }
    if (is === REASORT_IS.ANNULE) {
      return 'badge-light-danger';
    }
    return 'badge-light-info';
  }

  toggleFilter(): void {
    this.showFilter = !this.showFilter;
    if (this.showFilter) {
      this.draftDateDebut = this.dateDebut;
      this.draftDateFin = this.dateFin;
    }
    setTimeout(() => feather.replace(), 0);
  }

  applyFilters(): void {
    this.dateDebut = this.draftDateDebut;
    this.dateFin = this.draftDateFin;
    this.showFilter = false;
    this.currentPage = 1;
    this.loadPage();
  }

  resetFilters(): void {
    this.draftDateDebut = '';
    this.draftDateFin = '';
    this.dateDebut = '';
    this.dateFin = '';
    this.showFilter = false;
    this.currentPage = 1;
    this.loadPage();
  }

  getActiveFilters(): { key: string; label: string; value: string }[] {
    const filters: { key: string; label: string; value: string }[] = [];
    if (this.searchTerm.trim()) {
      filters.push({ key: 'search', label: 'Recherche', value: this.searchTerm.trim() });
    }
    if (this.dateDebut) {
      filters.push({ key: 'date_debut', label: 'Du', value: this.dateDebut });
    }
    if (this.dateFin) {
      filters.push({ key: 'date_fin', label: "Jusqu'à", value: this.dateFin });
    }
    return filters;
  }

  trackByFilterKey(_index: number, filter: { key: string }): string {
    return filter.key;
  }

  removeFilter(key: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (key === 'search') {
      this.searchTerm = '';
    } else if (key === 'date_debut') {
      this.dateDebut = '';
      this.draftDateDebut = '';
    } else if (key === 'date_fin') {
      this.dateFin = '';
      this.draftDateFin = '';
    }
    this.currentPage = 1;
    this.loadPage();
  }

  onSearch(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.loadPage();
    }, 300);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadPage();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadPage();
  }

  openDetail(row: ReasortTransfertTableRow, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    void this.router.navigate(['/achat/reassort/action/validation'], {
      queryParams: { id },
    });
  }

  private refreshIcons(): void {
    setTimeout(() => {
      try {
        feather.replace();
      } catch {
        /* noop */
      }
    }, 0);
  }

  private loadPage(): void {
    this.loadSub?.unsubscribe();
    this.loading = true;
    const apiTab = this.tab === 'clos' ? 'clos' : 'en_cours';
    this.loadSub = this.reasortService
      .list({
        tab: apiTab,
        page: this.currentPage,
        limit: this.pageSize,
        date_debut: this.dateDebut || undefined,
        date_fin: this.dateFin || undefined,
        mot_cle: this.searchTerm || undefined,
      })
      .subscribe({
        next: ({ items, pagination }) => {
          this.rows = items;
          this.serverTotal = pagination.total;
          this.currentPage = pagination.page;
          this.pageSize = pagination.limit;
          this.totalPages = Math.max(1, Math.ceil(pagination.total / Math.max(1, pagination.limit)));
          this.loading = false;
          this.refreshIcons();
        },
        error: (err: Error) => {
          this.rows = [];
          this.serverTotal = 0;
          this.totalPages = 1;
          this.loading = false;
          this.toastr.error(err?.message || 'Impossible de charger les réassorts', 'Erreur');
          this.refreshIcons();
        },
      });
  }
}
